import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Filter } from 'lucide-react';
import SEO, { breadcrumbSchema } from '@/components/SEO';
import CTASection from '@/components/CTASection';
import { getAllImages, portfolioCategories } from '@/data/portfolio-data';
import MasonryGallery from '@/components/portfolio/MasonryGallery';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const PortfolioAll = () => {
  const [activeCategory, setActiveCategory] = useState('tout');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const allItems = useMemo(() => getAllImages(), []);

  const filtered = useMemo(
    () =>
      activeCategory === 'tout'
        ? allItems
        : allItems.filter((i) => i.category === activeCategory),
    [activeCategory, allItems]
  );

  return (
    <>
      <SEO
        title="Toutes nos réalisations — Portfolio | Cygnature Production"
        description="L'ensemble de nos réalisations en images : mariages, cérémonies, corporate, événements et plus encore."
        path="/portfolio/tous"
        jsonLd={[breadcrumbSchema([
          { name: 'Accueil', path: '/' },
          { name: 'Portfolio', path: '/portfolio' },
          { name: 'Toutes nos réalisations', path: '/portfolio/tous' },
        ])]}
      />

      <section className="relative px-6 lg:px-8 py-24 md:py-32 bg-petrol text-petrol-foreground overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-petrol via-petrol/95 to-petrol" />
        <div className="relative max-w-4xl mx-auto text-center z-10">
          <Link
            to="/portfolio"
            className="inline-flex items-center gap-1.5 text-xs text-primary font-heading font-medium mb-6 hover:gap-2.5 transition-all"
          >
            <ArrowLeft size={12} /> Retour au portfolio
          </Link>
          <h1 className="font-heading text-3xl md:text-5xl font-bold">Toutes nos réalisations</h1>
          <p className="mt-3 text-sm text-petrol-foreground/50">
            {allItems.length} photo{allItems.length > 1 ? 's' : ''}
          </p>
        </div>
      </section>

      {/* Filter (Select) */}
      <section className="px-6 lg:px-8 pt-8 pb-2 bg-background">
        <div className="max-w-7xl mx-auto flex justify-center">
          <Select value={activeCategory} onValueChange={(v) => setActiveCategory(v)}>
            <SelectTrigger className="w-full sm:w-auto sm:min-w-[280px] h-11 rounded-xl bg-card border-border text-sm font-heading font-medium px-4 gap-3">
              <Filter size={15} className="text-primary shrink-0" strokeWidth={1.5} />
              <SelectValue />
            </SelectTrigger>
            <SelectContent side="bottom" align="center">
              <SelectItem value="tout">Toutes les catégories ({allItems.length})</SelectItem>
              {portfolioCategories.map((cat) => {
                const Icon = cat.icon;
                return (
                  <SelectItem key={cat.slug} value={cat.slug}>
                    <span className="inline-flex items-center gap-2">
                      <Icon size={14} className="text-primary" strokeWidth={1.5} />
                      {cat.name} ({cat.images.length})
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      </section>

      <section className="px-6 lg:px-8 py-12 md:py-16 bg-background">
        <div className="max-w-7xl mx-auto">
          <MasonryGallery key={activeCategory} images={filtered} pageSize={24} />
        </div>
      </section>

      <section className="px-6 lg:px-8 py-16 md:py-20 bg-petrol text-petrol-foreground">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold">
            Votre projet mérite la même attention
          </h2>
          <Link
            to="/contact"
            onClick={() => window.scrollTo(0, 0)}
            className="inline-flex items-center gap-2 mt-8 bg-primary text-primary-foreground rounded-full px-8 py-3.5 font-heading font-medium text-sm hover:bg-primary/90 transition-colors"
          >
            Parlons de votre projet <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default PortfolioAll;
